import {
  Drawer,
  Toolbar,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  IconButton,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import SummarizeTwoToneIcon from "@mui/icons-material/SummarizeTwoTone";
import CloseIcon from "@mui/icons-material/Close";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LockClockIcon from "@mui/icons-material/LockClock";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PeopleIcon from "@mui/icons-material/People";
import BarChartIcon from "@mui/icons-material/BarChart";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ListAltIcon from "@mui/icons-material/ListAlt";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";

import { SIDEBAR_WIDTH } from "../constants";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import FeaturedVideoIcon from "@mui/icons-material/FeaturedVideo";
import QuizIcon from "@mui/icons-material/Quiz";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import Person3Icon from "@mui/icons-material/Person3";

import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import PaidIcon from "@mui/icons-material/Paid";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import { PropsWithoutRef, useEffect, useState } from "react";
import useAuth from "../hooks/useAuth";
import useTranslation from "../hooks/Translation";

interface Props {
  mobileOpen: boolean;
  handleSidebarToggle: () => void;
}

export default function Sidebar({
  mobileOpen,
  handleSidebarToggle,
}: PropsWithoutRef<Props>) {
  const userData = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const translation = useTranslation() as any;

  const items = [
    {
      label: translation.Globals.dashboard,
      name: "Dashboard",
      icon: <DashboardIcon />,
      key: "1",
      selected: "/dashboard",
    },
    {
      label: translation.Globals.manage_user,
      name: "Manage Users",
      icon: <PeopleIcon />,
      key: "2",
      selected: "/manageusers",
    },
    {
      label: translation.Globals.manage_Advertiser,
      name: "Manage Advertisers",
      icon: <Person3Icon />,
      key: "3",
      selected: "/manage-advertisers",
    },
    {
      label: translation.Globals.manage_advertisement,
      name: "Manage Advertisements",
      icon: <FeaturedVideoIcon />,
      key: "4",
      selected: "/manage-advertisements",
    },
    {
      label: translation.Globals.manage_content,
      name: "Manage Content/Posts",
      icon: <ContentPasteIcon />,
      key: "5",
      selected: "/manage-content",
    },
    {
      label: translation.Globals.manage_journey,
      name: "Manage Journey",
      icon: <VideoLibraryIcon />,
      key: "6",
      selected: "/manage-journeys",
    },
    {
      label: translation.Globals.manage_challenge,
      name: "Manage Challenge Categories",
      icon: <ListAltIcon />,
      key: "7",
      selected: "/manage-categories",
    },

    {
      label: translation.Globals.manage_subs,
      name: "Manage Subscription Plans",
      icon: <SubscriptionsIcon />,
      key: "8",
      selected: "/manage-subscription",
    },

    {
      label: translation.Globals.manage_rewards,
      name: "Manage Rewards",
      icon: <EmojiEventsIcon />,
      key: "9",
      selected: "/manage-rewards",
    },
    {
      label: translation.Globals.customer,
      name: "Customer Support",
      icon: <HelpCenterIcon />,
      key: "10",
      selected: "/customer-support",
    },
    {
      label: translation.Globals.manage_challenge_dash,
      name: "Manage Challenges",
      icon: <ThumbUpIcon />,
      key: "11",
      selected: "/manage-challenges",
    },
    {
      label: translation.Globals.manage_word,
      name: "Manage Words",
      icon: <LockClockIcon />,
      key: "12",
      selected: "/manage-words",
    },
    {
      label: translation.Globals.manage_payment,
      name: "Manage Payments",
      icon: <PaidIcon />,
      key: "13",
      selected: "/manage-revenue",
    },
    {
      label: translation.Globals.manage_gift,
      name: "Manage Gifts",
      icon: <CardGiftcardIcon />,
      key: "14",
      selected: "/manage-gifts",
    },
    {
      label: translation.Globals.manage_notification,
      name: "Manage Notifications",
      icon: <NotificationsActiveIcon />,
      key: "15",
      selected: "/manage-notifications",
    },
    {
      label: translation.Globals.report,
      name: "Reports and Analytics",
      icon: <BarChartIcon />,
      key: "16",
      selected: "/analytics",
    },
    {
      label: translation.Globals.manage_subadmin,
      name: "Manage Sub-Admin",
      icon: <PersonAddIcon />,
      key: "17",
      selected: "/manage-subAdmin",
    },
    {
      label: translation.Globals.cms,
      name: "Manage CMS",
      icon: <SummarizeTwoToneIcon />,
      key: "18",
      selected: "/cms",
    },
    {
      label: translation.Globals.faq,
      name: "Manage FAQs",
      icon: <QuizIcon />,
      key: "19",
      selected: "/faq",
    },
  ];
  const [list, setList] = useState<any>(
    userData?.role === "ADMIN" ? items : userData?.permission
  );

  useEffect(() => {
    let arr = [];
    if (userData?.role === "ADMIN") {
      setList(items);
    } else if (userData?.permission?.length) {
      const permission = userData?.permission;
      arr = items.map((item) => {
        let idx = -1;
        idx = permission?.findIndex((ele: any) => ele?.label === item?.name);
        if (idx > -1) {
          if (permission[idx].isView) {
            return item;
          }
          return false;
        }
      });
      setList(arr);
    }
  }, [userData]);

  return (
    <Box
      component="nav"
      sx={{ flexShrink: { lg: 0 } }}
      className="sidebar-main-div"
    >
      <Drawer
        className="sidebar_drawer"
        variant="temporary"
        open={mobileOpen}
        onClose={handleSidebarToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: SIDEBAR_WIDTH,
          },
        }}
      >
        <Toolbar className="sidebar-logo" sx={{ position: "relative" }}>
          <figure>
            <img
              src="/static/images/logo.png"
              alt=""
              onClick={() => navigate("/dashboard")}
            />
          </figure>
          <IconButton
            sx={{
              position: "absolute",
              top: "10px",
              right: "10px",
              color: "white",
              display: { lg: "none" },
            }}
            onClick={handleSidebarToggle}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
        {list?.length
          ? list
              .filter((ele: any) => ele !== undefined)
              .map((item: any) => {
                const isSelected = location.pathname.includes(item?.selected);
                return (
                  <List
                    key={item?.name}
                    className="sidebr-lst"
                    sx={{ padding: 0 }}
                  >
                    <ListItemButton
                      className={`lst-itm ${isSelected ? "Mui-selected" : ""}`}
                      onClick={() => navigate(item?.selected)}
                    >
                      <ListItemIcon>{item?.icon}</ListItemIcon>
                      <ListItemText
                        className="lstitm-txt"
                        primary={item?.label}
                      />
                    </ListItemButton>
                  </List>
                );
              })
          : null}
      </Drawer>
      <Drawer
        variant="permanent"
        className="sidebar_drawer"
        sx={{
          display: { xs: "none", lg: "block" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
          },
        }}
        open
      >
        <Toolbar className="sidebar-logo" sx={{ position: "relative" }}>
          <figure>
            <img
              src="/static/images/logo.png"
              alt=""
              onClick={() => navigate("/dashboard")}
            />
          </figure>
          <IconButton
            sx={{
              position: "absolute",
              top: "10px",
              right: "10px",
              color: "white",
              display: { lg: "none" },
            }}
            onClick={handleSidebarToggle}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
        {list?.length
          ? list
              .filter((ele: any) => ele !== undefined)
              .map((item: any) => {
                const isSelected = location.pathname.includes(item?.selected);
                return (
                  <List
                    key={item?.name}
                    className="sidebr-lst"
                    sx={{ padding: 0 }}
                  >
                    <ListItemButton
                      className={`lst-itm ${isSelected ? "Mui-selected" : ""}`}
                      onClick={() => navigate(item?.selected)}
                    >
                      <ListItemIcon>{item?.icon}</ListItemIcon>
                      <ListItemText
                        className="lstitm-txt"
                        primary={item?.label}
                      />
                    </ListItemButton>
                  </List>
                );
              })
          : null}
      </Drawer>
    </Box>
  );
}
